import styled, { css } from 'styled-components';
import {
  FONT_SIZES,
  useFontWeightCss,
  useFontSizeCss,
  useTypographyCss,
  IDisplayCssProps,
  DisplayCss,
  useDisplayCss,
  ITypographyProps,
  FONT_WEIGHTS,
  FontSize
} from '../../fundamentals/typography/typography';
import { useGutterCss, IGutterProps, SPACE_DEFAULT } from '../../fundamentals/spaces/space';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { INoTextDecorationProps, useActionOnTextCss } from '../../fundamentals/actions';
import { BaseA, BaseP, BaseSpan } from '../../common/BaseElements';
import { TYPOGRAPHY } from '../../fundamentals/typography/eoraTypography';
import { ITypographyCss, displayTypes, useEoraCss, useEoraMobileCss } from '../../fundamentals/typography/typographyEora';

const DEFAULT_SIZE = 'DEFAULT';
const DEFAULT_OVERLINE_SIZE = 'SMALL';
const MOB_DEFAULT_SIZE = 'MOB_DEFAULT';
export type TextEoraSizeMobile = 'MOB_LARGE' | 'MOB_DEFAULT' | 'MOB_SMALL';

const toMobileFontSize = (size: 'DEFAULT' | 'SMALL' | 'LARGE' | 'MICRO' | 'NANO' | 'PICO' | 'FEMTO' | undefined): TextEoraSizeMobile => {
  let mobileSize: TextEoraSizeMobile = 'MOB_SMALL';
  switch (size) {
    case 'LARGE': {
      mobileSize = 'MOB_LARGE';
      break;
    }
    case 'DEFAULT':
    case undefined: {
      mobileSize = MOB_DEFAULT_SIZE;
      break;
    }
  }
  return mobileSize;
};

export type ITextSize =
  | typeof FONT_SIZES.TEXT_NANO
  | typeof FONT_SIZES.TEXT_MICRO
  | typeof FONT_SIZES.TEXT_SMALL
  | typeof FONT_SIZES.TEXT_DEFAULT
  | typeof FONT_SIZES.TEXT_LARGE;

export type IParagraphSize = keyof typeof TYPOGRAPHY.PARAGRAPH;

export interface IFontSizesProps {
  textSize?: FontSize;
}

export interface ITextEoraProps {
  textEoraSize?: IParagraphSize;
  textStyle?: keyof (typeof TYPOGRAPHY.PARAGRAPH)[IParagraphSize];
  underlined?: boolean;
  lineHeight?: number;
}

export interface ITextProps extends ITextEoraProps, IFontSizesProps, ITypographyProps, INoTextDecorationProps {}

export const useTextCss = css<ITextProps>`
  ${useTypographyCss};
  ${useActionOnTextCss};

  ${({ textEoraSize, textStyle, underlined, lineHeight, asDisplay, textSize = FONT_SIZES.TEXT_DEFAULT }) => {
    if (textStyle) {
      return asDisplay
        ? css`
            ${useEoraCss(TYPOGRAPHY.DISPLAY[displayTypes[asDisplay]][textStyle], underlined, lineHeight)}
          `
        : css`
            ${useEoraCss(TYPOGRAPHY.PARAGRAPH[textEoraSize ?? DEFAULT_SIZE][textStyle], underlined, lineHeight)}
            ${useEoraMobileCss(TYPOGRAPHY.MOBILE.MOB_PARAGRAPH[toMobileFontSize(textEoraSize) ?? MOB_DEFAULT_SIZE][textStyle])}
          `;
    } else {
      return css`
        ${useFontWeightCss(asDisplay ? FONT_WEIGHTS.SEMI_BOLD : undefined)}
        ${useFontSizeCss({ [SIZES.X_SMALL]: textSize })};
      `;
    }
  }}
`;

export const P = styled(BaseP)<ITextProps>`
  ${useGutterCss({ bottom: SPACE_DEFAULT })};
  ${useTextCss};
  ${useDisplayCss(DisplayCss.Block)};
`;

// Paired with `ITextProps`
export const useCommonTextCss = css`
  ${useGutterCss()};
  ${useTextCss};
  ${useDisplayCss()};
`;

// Cancel gutter in P tags
export const useZeroPaddingOnChildPsCss = css`
  ${P} {
    ${useGutterCss()};
  }
`;

export const Span = styled(BaseSpan)<ITextProps>`
  ${useCommonTextCss};
`;

export const A = styled(BaseA)<ITextProps>`
  ${useCommonTextCss};
  color: ${({ theme }) => theme.colours.links.colour};
  &:hover {
    color: ${({ theme }) => theme.colours.links.hover};
  }
  &:active {
    color: ${({ theme }) => theme.colours.links.active};
  }
  &:visited {
    color: ${({ theme }) => theme.colours.links.visited};
  }
`;

export type OverlineSize = typeof FONT_SIZES.TEXT_MICRO | typeof FONT_SIZES.TEXT_SMALL | typeof FONT_SIZES.TEXT_NANO | typeof FONT_SIZES.TEXT_DEFAULT;

const OverlineSizes: Record<OverlineSize, ITypographyCss> = {
  textSmall: TYPOGRAPHY.OVERLINE[DEFAULT_OVERLINE_SIZE],
  textMicro: TYPOGRAPHY.OVERLINE.MICRO,
  textNano: TYPOGRAPHY.OVERLINE['NANO_*LH10'],
  textDefault: TYPOGRAPHY.PARAGRAPH.DEFAULT.SEMI_BOLD
};

export interface IOverlineProps extends IGutterProps, IDisplayCssProps {
  small?: boolean;
  size?: OverlineSize;
  overlineSize?: keyof typeof TYPOGRAPHY.OVERLINE;
}

export const Overline = styled(BaseSpan)<IOverlineProps>`
  ${useGutterCss()};
  ${useDisplayCss()}
  text-transform: uppercase;
  font-weight: ${({ theme }) => theme.fonts.fontWeights[FONT_WEIGHTS.SEMI_BOLD]};
  ${useActionOnTextCss};

  ${({ size }) => size && useEoraCss(OverlineSizes[size])}
  ${({ small, size, overlineSize }) =>
    !size && (small ? useEoraCss(TYPOGRAPHY.OVERLINE.MICRO) : useEoraCss(TYPOGRAPHY.OVERLINE[overlineSize ?? DEFAULT_OVERLINE_SIZE]))};
`;
